import { Link } from "react-router-dom";
import { BTN_SOLID, CHIP_TALL } from "../homeTokens";

/**
 * "Suas tarefas" — the account's open loops, in one list.
 *
 * This is where everything below interrupt-level lands. It is the pressure
 * valve that lets the layout stay fixed: a new signal type becomes another row
 * here instead of another card fighting for space above.
 *
 * Rows are dismissible because an actionable list the user cannot clear stops
 * being a list and becomes wallpaper. Blocking rows are the exception — they
 * carry no dismiss affordance, since hiding them would hide a stopped
 * operation.
 */

const TONE = {
  blocking: {
    tag: "bg-[rgba(217,134,19,.12)] text-[#b06d0c] dark:bg-[rgba(255,164,32,.18)] dark:text-[#ffbe63]",
    icon: "bg-[rgba(217,134,19,.10)] border-[rgba(217,134,19,.26)] dark:bg-[rgba(255,164,32,.14)] dark:border-[rgba(255,164,32,.3)]",
  },
  billing: {
    tag: "bg-[rgba(224,72,58,.12)] text-[#c23b2f] dark:bg-[rgba(255,90,77,.18)] dark:text-[#ff8b7f]",
    icon: "bg-[rgba(224,72,58,.10)] border-[rgba(224,72,58,.26)] dark:bg-[rgba(255,90,77,.14)] dark:border-[rgba(255,90,77,.3)]",
  },
  ticket: {
    tag: "bg-[rgba(91,91,214,.12)] text-[#4f4fc4] dark:bg-[rgba(139,139,255,.18)] dark:text-[#b0b0ff]",
    icon: "bg-[rgba(91,91,214,.10)] border-[rgba(91,91,214,.26)] dark:bg-[rgba(139,139,255,.14)] dark:border-[rgba(139,139,255,.3)]",
  },
  automation: {
    tag: "bg-[rgba(18,156,104,.12)] text-[#0f8a5c] dark:bg-[rgba(92,214,178,.16)] dark:text-[#7ee2a8]",
    icon: "bg-[rgba(18,156,104,.10)] border-[rgba(18,156,104,.24)] dark:bg-[rgba(92,214,178,.12)] dark:border-[rgba(92,214,178,.26)]",
  },
  neutral: {
    tag: "bg-black/[.06] text-[#5c5f70] dark:bg-white/[.09] dark:text-white/65",
    icon: "bg-black/[.04] border-black/[.10] dark:bg-white/[.06] dark:border-white/[.12]",
  },
};

export default function TaskList({ tasks = [], onDismiss, onAction }) {
  if (!tasks.length) return null;

  const badgeTone = tasks.some((t) => t.tone === "blocking")
    ? "bg-[#d98613] text-white"
    : tasks.some((t) => t.tone === "billing")
      ? "bg-[#e0483a] text-white"
      : "bg-[#15161f] text-white dark:bg-white dark:text-[#0a0a0f]";

  return (
    <div className="pt-11">
      <div className="flex items-center gap-2.5 px-1 pb-3.5">
        <h2 className="text-[14.5px] font-semibold leading-none text-[#15161f] dark:text-white/90">
          Suas tarefas
        </h2>
        <span
          className={`inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-[9px] px-1.5 text-[10px] font-extrabold leading-none ${badgeTone}`}
        >
          {tasks.length}
        </span>
        <Link
          to="/internal/support"
          className="ml-auto whitespace-nowrap text-xs font-semibold leading-none text-[#5c5f70] transition-colors hover:text-[#15161f] dark:text-white/55 dark:hover:text-white"
        >
          Ver todos os chamados →
        </Link>
      </div>

      <ul className="overflow-hidden rounded-[18px] border border-black/[.07] bg-white/70 dark:border-white/[.07] dark:bg-white/[.035]">
        {tasks.map((task, index) => {
          const tone = TONE[task.tone] || TONE.neutral;
          const isLast = index === tasks.length - 1;
          return (
            <li
              key={task.id}
              className={`flex items-center gap-3.5 px-[18px] py-4 ${
                isLast ? "" : "border-b border-black/[.06] dark:border-white/[.06]"
              }`}
            >
              <span
                aria-hidden="true"
                className={`h-[30px] w-[30px] flex-none rounded-[9px] border ${tone.icon}`}
              />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2.5">
                  <span className="text-[13.5px] font-semibold leading-[1.35] text-[#15161f] dark:text-white">
                    {task.title}
                  </span>
                  <span
                    className={`flex-none whitespace-nowrap rounded-md px-2 py-[3px] text-[9px] font-bold leading-[1.4] tracking-[.08em] ${tone.tag}`}
                  >
                    {task.tag}
                  </span>
                </div>
                <div className="mt-[3px] text-[12.5px] font-normal leading-[1.5] text-[#5c5f70] dark:text-white/45">
                  {task.subtitle}
                </div>
              </div>
              <button
                type="button"
                onClick={() => onAction?.(task)}
                className={task.primary ? BTN_SOLID : CHIP_TALL}
              >
                {task.cta}
              </button>
              {task.dismissible !== false && (
                <button
                  type="button"
                  onClick={() => onDismiss?.(task)}
                  aria-label={`Dispensar: ${task.title}`}
                  className="h-7 w-7 flex-none rounded-lg border-0 bg-transparent text-[13px] text-[#9a9dad] transition-colors hover:bg-black/5 hover:text-[#15161f] dark:text-white/30 dark:hover:bg-white/10 dark:hover:text-white/70"
                >
                  ✕
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
